import React, { useMemo, useState, useEffect } from 'react';
import { Select, Switch, Tag } from 'antd';

export interface SelectThamChieuLuongCoBanBSTBKSProps {
    dataSource?: any[];
    value?: string[];
    onChange?: (value: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
}

interface MonthGroup {
    key: string; // e.g. "2026_2"
    nam: number;
    thang: number;
    label: string; // e.g. "Tháng 2/2026"
    tamUngItem?: any;
    dieuChinhItem?: any;
    hasTamUng: boolean;
    hasDieuChinh: boolean;
}

const SelectThamChieuLuongCoBanBSTBKS: React.FC<SelectThamChieuLuongCoBanBSTBKSProps> = ({
    dataSource = [],
    value = [],
    onChange,
    placeholder = 'Chọn kỳ lương cơ bản',
    disabled = false
}) => {
    // 1. Gom nhóm danh sách lương cơ bản theo từng tháng / năm
    const monthGroups: MonthGroup[] = useMemo(() => {
        const groupMap = new Map<string, MonthGroup>();

        (dataSource || []).forEach((item: any) => {
            const thang = item.thang ?? (item.thangNam ? new Date(item.thangNam).getMonth() + 1 : 0);
            const nam = item.nam ?? (item.thangNam ? new Date(item.thangNam).getFullYear() : 0);
            const key = `${nam}_${thang}`;

            let group = groupMap.get(key);
            if (!group) {
                group = {
                    key,
                    nam,
                    thang,
                    label: `Tháng ${thang}/${nam}`,
                    hasTamUng: false,
                    hasDieuChinh: false
                };
                groupMap.set(key, group);
            }

            if (item.loai === 0) {
                // 0 = Điều chỉnh
                group.dieuChinhItem = item;
                group.hasDieuChinh = true;
            } else {
                // 1 = Tạm ứng (hoặc mặc định)
                group.tamUngItem = item;
                group.hasTamUng = true;
            }
        });

        // Sắp xếp giảm dần theo năm, sau đó giảm dần theo tháng
        return Array.from(groupMap.values()).sort((a, b) => {
            if (b.nam !== a.nam) return b.nam - a.nam;
            return b.thang - a.thang;
        });
    }, [dataSource]);

    // 2. Trạng thái Switch cho mỗi tháng: true = Điều chỉnh, false = Tạm ứng
    const [switchStates, setSwitchStates] = useState<Record<string, boolean>>({});

    // Đồng bộ switchStates khi `value` từ ngoài truyền vào (ví dụ mở modal sửa đã có dữ liệu lưu trước đó)
    useEffect(() => {
        if (!value || value.length === 0) return;
        const currentValSet = new Set((value || []).map((x) => x?.toString()));

        setSwitchStates((prev) => {
            let changed = false;
            const next = { ...prev };

            monthGroups.forEach((g) => {
                if (g.dieuChinhItem && currentValSet.has(g.dieuChinhItem.id.toString())) {
                    if (next[g.key] !== true) {
                        next[g.key] = true;
                        changed = true;
                    }
                } else if (g.tamUngItem && currentValSet.has(g.tamUngItem.id.toString())) {
                    if (next[g.key] !== false) {
                        next[g.key] = false;
                        changed = true;
                    }
                }
            });

            return changed ? next : prev;
        });
    }, [value, monthGroups]);

    // 3. Xác định các tháng nào đang được chọn dựa trên `value`
    const selectedMonthKeys = useMemo(() => {
        const currentValSet = new Set((value || []).map((x) => x?.toString()));
        return monthGroups
            .filter((g) => {
                const hasTamUngSelected = g.tamUngItem && currentValSet.has(g.tamUngItem.id.toString());
                const hasDieuChinhSelected = g.dieuChinhItem && currentValSet.has(g.dieuChinhItem.id.toString());
                return hasTamUngSelected || hasDieuChinhSelected;
            })
            .map((g) => g.key);
    }, [value, monthGroups]);

    // Tập hợp ID đã biết từ dataSource
    const allKnownIds = useMemo(() => {
        const set = new Set<string>();
        monthGroups.forEach((g) => {
            if (g.tamUngItem) set.add(g.tamUngItem.id.toString());
            if (g.dieuChinhItem) set.add(g.dieuChinhItem.id.toString());
        });
        return set;
    }, [monthGroups]);

    // Tính toán danh sách ID kết quả từ danh sách tháng được chọn và trạng thái switch hiện tại
    const computeSelectedIds = (
        monthKeys: string[],
        currentSwitchMap: Record<string, boolean>
    ): string[] => {
        // Giữ lại các ID không thuộc dataSource hiện tại (ví dụ từ năm khác chưa tải)
        const foreignIds = (value || []).map(String).filter((id) => !allKnownIds.has(id));

        const groupIds: string[] = [];
        monthKeys.forEach((mKey) => {
            const g = monthGroups.find((x) => x.key === mKey);
            if (!g) return;

            const isDieuChinh = currentSwitchMap[g.key] ?? (g.hasDieuChinh && !g.hasTamUng);

            if (isDieuChinh && g.dieuChinhItem) {
                groupIds.push(g.dieuChinhItem.id.toString());
            } else if (!isDieuChinh && g.tamUngItem) {
                groupIds.push(g.tamUngItem.id.toString());
            } else if (g.dieuChinhItem) {
                groupIds.push(g.dieuChinhItem.id.toString());
            } else if (g.tamUngItem) {
                groupIds.push(g.tamUngItem.id.toString());
            }
        });

        return [...foreignIds, ...groupIds];
    };

    // Khi người dùng chọn/bỏ chọn tháng trên Select
    const handleSelectChange = (newSelectedKeys: string[]) => {
        const nextIds = computeSelectedIds(newSelectedKeys, switchStates);
        onChange?.(nextIds);
    };

    // Khi người dùng gạt switch Tạm ứng / Điều chỉnh
    const handleToggleSwitch = (g: MonthGroup, checked: boolean) => {
        const newSwitchMap = { ...switchStates, [g.key]: checked };
        setSwitchStates(newSwitchMap);

        // Nếu tháng này chưa được chọn, gạt switch cũng sẽ tự động chọn tháng đó với kiểu tương ứng
        let targetMonthKeys = selectedMonthKeys;
        if (!selectedMonthKeys.includes(g.key)) {
            targetMonthKeys = [...selectedMonthKeys, g.key];
        }

        const nextIds = computeSelectedIds(targetMonthKeys, newSwitchMap);
        onChange?.(nextIds);
    };

    // Render tag hiển thị trong ô Select
    const tagRender = (props: any) => {
        const { value: monthKey, closable, onClose } = props;
        const g = monthGroups.find((x) => x.key === monthKey);
        if (!g) return <span>{props.label}</span>;

        const isDieuChinh = switchStates[g.key] ?? (g.hasDieuChinh && !g.hasTamUng);
        const hasBoth = g.hasTamUng && g.hasDieuChinh;

        return (
            <Tag
                color={isDieuChinh ? 'orange' : 'blue'}
                closable={closable && !disabled}
                onClose={onClose}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    margin: '2px 4px 2px 0',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}
            >
                <span style={{ fontWeight: 500, marginRight: 4 }}>{g.label}</span>
                <span
                    onClick={(e) => {
                        if (hasBoth && !disabled) {
                            e.stopPropagation();
                            handleToggleSwitch(g, !isDieuChinh);
                        }
                    }}
                    title={hasBoth && !disabled ? 'Nhấn để chuyển đổi giữa Tạm ứng và Điều chỉnh' : undefined}
                    style={{
                        fontSize: '11px',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        background: isDieuChinh ? '#fa8c16' : '#1890ff',
                        color: '#fff',
                        cursor: hasBoth && !disabled ? 'pointer' : 'default',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        userSelect: 'none'
                    }}
                >
                    {isDieuChinh ? 'Điều chỉnh' : 'Tạm ứng'}
                    {hasBoth && !disabled && <span style={{ fontSize: '10px' }}>⇄</span>}
                </span>
            </Tag>
        );
    };

    return (
        <Select
            mode="multiple"
            disabled={disabled}
            style={{ width: '100%' }}
            placeholder={placeholder}
            value={selectedMonthKeys}
            onChange={handleSelectChange}
            tagRender={tagRender}
            optionLabelProp="labelText"
            filterOption={(input, option) => {
                const text = (option as any)?.labelText || '';
                return text.toLowerCase().includes((input || '').toLowerCase());
            }}
        >
            {monthGroups.map((g) => {
                const isDieuChinh = switchStates[g.key] ?? (g.hasDieuChinh && !g.hasTamUng);

                return (
                    <Select.Option key={g.key} value={g.key} labelText={g.label}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: '2px 0'
                            }}
                        >
                            <span style={{ fontWeight: 500 }}>{g.label}</span>

                            {g.hasTamUng && g.hasDieuChinh ? (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <Switch
                                        size="small"
                                        checkedChildren="Điều chỉnh"
                                        unCheckedChildren="Tạm ứng"
                                        checked={isDieuChinh}
                                        onChange={(checked, e) => {
                                            e?.stopPropagation();
                                            handleToggleSwitch(g, checked);
                                        }}
                                    />
                                </div>
                            ) : g.hasDieuChinh ? (
                                <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                                    Điều chỉnh
                                </Tag>
                            ) : (
                                <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                                    Tạm ứng
                                </Tag>
                            )}
                        </div>
                    </Select.Option>
                );
            })}
        </Select>
    );
};

export default SelectThamChieuLuongCoBanBSTBKS;
